import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { usePublicPage } from "../../../hooks/usePublicPage";
import { resolveMediaUrl } from "../../../utils/mediaUrl";
import { getApiErrorMessage } from "../../../utils/apiResponse";
import { useLanguage } from "../../../context/LanguageContext";
import { usePrefetchPublicRoute } from "../../../hooks/usePrefetchPublicRoute";
import "./Footer.css";

const requireField = (fields, key) => {
  const value = fields?.[key];
  if (value == null || String(value).trim() === "") {
    throw new Error(`Footer field "${key}" is missing from the API response.`);
  }
  return value;
};

const requireMediaUrl = (section, groupKey) => {
  const item = section.media?.groups?.[groupKey]?.[0] || null;
  if (!item?.url) {
    throw new Error(`Footer media group "${groupKey}" is missing from the API response.`);
  }
  return resolveMediaUrl(item.url);
};

const buildFooterModel = (section) => {
  const f = section.fields || {};

  return {
    ctaTitle: requireField(f, "ctaTitle"),
    ctaText: requireField(f, "ctaText"),
    trackButtonText: requireField(f, "trackButtonText"),
    trackButtonUrl: requireField(f, "trackButtonUrl"),
    reportButtonText: requireField(f, "reportButtonText"),
    reportButtonUrl: requireField(f, "reportButtonUrl"),
    logoAlt: requireField(f, "logoAlt"),
    tagline: requireField(f, "tagline"),
    logoUrl: requireMediaUrl(section, "logo"),
    exploreTitle: requireField(f, "exploreTitle"),
    exploreLinks: [
      { href: requireField(f, "exploreAboutUrl"), label: requireField(f, "exploreAboutLabel") },
      { href: requireField(f, "exploreLawUrl"), label: requireField(f, "exploreLawLabel") },
      { href: requireField(f, "exploreNewsUrl"), label: requireField(f, "exploreNewsLabel") },
    ],
    reportTitle: requireField(f, "reportTitle"),
    reportLinks: [
      { href: requireField(f, "reportSubmitUrl"), label: requireField(f, "reportSubmitLabel") },
      { href: requireField(f, "reportTrackUrl"), label: requireField(f, "reportTrackLabel") },
      { href: requireField(f, "reportContactUrl"), label: requireField(f, "reportContactLabel") },
    ],
    legalTitle: requireField(f, "legalTitle"),
    legalLinks: [
      {
        href: requireField(f, "legalWhistleblowerUrl"),
        label: requireField(f, "legalWhistleblowerLabel"),
      },
      { href: requireField(f, "legalRightsUrl"), label: requireField(f, "legalRightsLabel") },
    ],
    copyright: requireField(f, "copyright"),
    social: [
      {
        href: requireField(f, "socialXUrl"),
        label: requireField(f, "socialXLabel"),
        icon: requireMediaUrl(section, "social-x"),
      },
      {
        href: requireField(f, "socialLinkedInUrl"),
        label: requireField(f, "socialLinkedInLabel"),
        icon: requireMediaUrl(section, "social-linkedin"),
      },
      {
        href: requireField(f, "socialFacebookUrl"),
        label: requireField(f, "socialFacebookLabel"),
        icon: requireMediaUrl(section, "social-facebook"),
      },
    ],
  };
};

const isExternal = (href) => /^https?:\/\//i.test(href);

const FooterLink = ({ href, children, className, prefetchRoute, ...rest }) => {
  const prefetchHandlers =
    prefetchRoute && !isExternal(href)
      ? {
          onMouseEnter: () => prefetchRoute(href),
          onFocus: () => prefetchRoute(href),
        }
      : {};

  if (isExternal(href)) {
    return (
      <a href={href} className={className} target="_blank" rel="noreferrer" {...rest}>
        {children}
      </a>
    );
  }

  return (
    <Link to={href} className={className} {...prefetchHandlers} {...rest}>
      {children}
    </Link>
  );
};

const Footer = () => {
  const { meta } = useLanguage();
  const prefetchRoute = usePrefetchPublicRoute();
  const pageQuery = usePublicPage("shared");

  const { model, buildError } = useMemo(() => {
    if (!pageQuery.data) {
      return { model: null, buildError: null };
    }

    try {
      const footer = pageQuery.data.sections?.find((s) => s.key === "footer");
      if (!footer) {
        throw new Error("Footer section was not found on the shared page.");
      }
      return { model: buildFooterModel(footer), buildError: null };
    } catch (err) {
      return {
        model: null,
        buildError: err instanceof Error ? err.message : String(err),
      };
    }
  }, [pageQuery.data]);

  const error = pageQuery.error != null
    ? getApiErrorMessage(pageQuery.error)
    : buildError;
  const loading = pageQuery.isPending && !model;

  if (loading) {
    return (
      <footer className="footer" dir={meta.dir}>
        <div className="footer__main">
          <div className="footer__container">
            <p style={{ color: "#64748b", fontSize: 14 }}>Loading footer…</p>
          </div>
        </div>
      </footer>
    );
  }

  if (error || !model) {
    return (
      <footer className="footer" dir={meta.dir}>
        <div className="footer__main">
          <div className="footer__container">
            <p style={{ color: "#b91c1c", fontSize: 14 }} role="alert">
              {error || "Footer could not be loaded."}
            </p>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="footer" dir={meta.dir}>
      <div className="footer__cta">
        <div className="footer__cta-container">
          <h2 className="footer__cta-title">{model.ctaTitle}</h2>
          <p className="footer__cta-text">{model.ctaText}</p>
          <div className="footer__cta-actions">
            <FooterLink href={model.trackButtonUrl} className="footer__btn-secondary" prefetchRoute={prefetchRoute}>
              {model.trackButtonText}
            </FooterLink>
            <FooterLink href={model.reportButtonUrl} className="footer__btn-primary" prefetchRoute={prefetchRoute}>
              {model.reportButtonText}
            </FooterLink>
          </div>
        </div>
      </div>

      <div className="footer__main">
        <div className="footer__container">
          <div className="footer__grid">
            <div className="footer__brand">
              <Link to="/" className="footer__logo" {...{
                onMouseEnter: () => prefetchRoute("/"),
                onFocus: () => prefetchRoute("/"),
              }}>
                <img src={model.logoUrl} alt={model.logoAlt} />
              </Link>
              <p className="footer__tagline">{model.tagline}</p>
            </div>

            <div className="footer__column">
              <h3 className="footer__column-title">{model.exploreTitle}</h3>
              <ul className="footer__links">
                {model.exploreLinks.map((link) => (
                  <li key={`${link.href}-${link.label}`}>
                    <FooterLink href={link.href} prefetchRoute={prefetchRoute}>{link.label}</FooterLink>
                  </li>
                ))}
              </ul>
            </div>

            <div className="footer__column">
              <h3 className="footer__column-title">{model.reportTitle}</h3>
              <ul className="footer__links">
                {model.reportLinks.map((link) => (
                  <li key={`${link.href}-${link.label}`}>
                    <FooterLink href={link.href} prefetchRoute={prefetchRoute}>{link.label}</FooterLink>
                  </li>
                ))}
              </ul>
            </div>

            <div className="footer__column">
              <h3 className="footer__column-title">{model.legalTitle}</h3>
              <ul className="footer__links">
                {model.legalLinks.map((link) => (
                  <li key={`${link.href}-${link.label}`}>
                    <FooterLink href={link.href} prefetchRoute={prefetchRoute}>{link.label}</FooterLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="footer__bottom">
        <div className="footer__bottom-container">
          <p className="footer__copyright">{model.copyright}</p>

          <div className="footer__social">
            {model.social.map((social) => (
              <FooterLink
                key={social.label}
                href={social.href}
                className="footer__social-link"
                aria-label={social.label}
              >
                <img src={social.icon} alt="" aria-hidden="true" />
              </FooterLink>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
